import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelHeader, PanelBody } from "../../../components/panel/panel";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import ServiceTimeModal from "../serviceTimeModel";
import ImageUpload from "../../../components/CustomComponents/ImageUpload/imageUpload";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import swal from "sweetalert";
import "../../../components/CustomComponents/Switchery/ToggleSwitch.css";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../staff/dropdownStyles";
import ScheduleManager from "../../../components/CustomComponents/TimeSchedule/weeklySlots";
import schedule from "../../../components/CustomComponents/TimeSchedule/schedule";
import CommonTimeSlotForAllWeek from "../../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek";
import CombinedDateTimePicker from "../../../components/CustomComponents/DateTimePicker/CombinedDateTimePicker";
import moment from "moment";
import TableQrModal from "../../../components/CustomComponents/TableQr/TableQrModal";
import SaveActions from "../../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../../hooks/useUnsavedChangesGuard.js";

function UpdateDiningArea() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { sA_ID } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrTableId, setQrTableId] = useState(null);
  const [qrBulkMode, setQrBulkMode] = useState(false);
  
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const detailEditorModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "blockquote"],
      ["clean"],
    ],
  };
  
  const [servicePeriods, setServicePeriods] = useState([]);
  const [isServiceTimeModalOpen, setIsServiceTimeModalOpen] = useState(false);
  const [selectedServiceTimes, setSelectedServiceTimes] = useState([]);
  const [imageState, setImageState] = useState({
    imageUrl: "",
    imageFile: null,
  });
  const [formData, setFormData] = useState();
  
  // Advance Deposit Rules state
  const [applyRules, setApplyRules] = useState(false);
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [compCurrency, setCompCurrency] = useState("GBP");
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [initialLoadedDataSnapshot, setInitialLoadedDataSnapshot] = useState(null);
  
  // Helper functions for time conversion
  const toStateTimeHHmm = (timeStr) => {
    if (!timeStr) return "";
    const trimmed = String(timeStr).trim();
    const parsed = moment(trimmed, ["h:mm A", "hh:mm A", "H:mm", "HH:mm"], true);
    if (!parsed.isValid()) return trimmed;
    return parsed.format("HH:mm");
  };

  const toPayloadTime12Hour = (timeStr) => {
    if (!timeStr) return "";
    const trimmed = String(timeStr).trim();
    const parsed = moment(trimmed, ["HH:mm", "H:mm", "h:mm A", "hh:mm A"], true);
    if (!parsed.isValid()) return trimmed;
    return parsed.format("h:mm A");
  };

  const depositTypeOptions = [
    { value: "FixedAmount", label: "Fixed Amount" },
    { value: "PerPerson", label: "Per Person" },
  ];

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const fetchServicePeriods = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    try {
      if (!silent) setLoading(true);
      const response = await axiosInstance.get(
        `/api/ServiceTime/GetAllServiceTimes`,
        {
          params: { CompID },
        }
      );
      setServicePeriods(response.data.data || []);
    } catch (error) {
      showErrorToast("Failed to load service periods");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [CompID]);

  const fetchDiningAreaData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/ServiceArea/GetServiceAreaByID`,
        {
          params: { CompID, ID: sA_ID },
        }
      );

      const areaData = response.data.data || response.data;
      // Dining Area Data logged

      if (
        !areaData.serviceAreaTables ||
        areaData.serviceAreaTables.length === 0
      ) {
        areaData.serviceAreaTables = [
          {
            t_ID: 0,
            localizeID: "en",
            t_Name: "",
            t_NoOfSeats: "",
            t_Shape: "",
            t_Status: "Available",
            t_Publish: true,
            t_Remarks: "",
            t_Xaxis: 0,
            t_Yaxis: 0,
            t_Version: 1,
            updatedateTime: new Date().toISOString(),
            compID: CompID,
            sA_ID: sA_ID,
            isTableMerged: false,
            mergedTableID: null,
            t_OriginalStatus: "Available",
          },
        ];
      } else {
        // Set t_OriginalStatus for each table row from DB
        areaData.serviceAreaTables = areaData.serviceAreaTables.map((table) => ({
          ...table,
          t_OriginalStatus: table.t_Status,
        }));
      }

      // Initialize reservation capabilities if not present
      const initializedData = {
        ...areaData,
        isRegularReservationCapable: areaData.isRegularReservationCapable ?? true,
        isPrivateReservationCapable: areaData.isPrivateReservationCapable ?? false,
        isEventCapable: areaData.isEventCapable ?? false,
        privateMinGuests: areaData.privateMinGuests ?? "",
        privateMaxGuests: areaData.privateMaxGuests ?? "",
        eventMinGuests: areaData.eventMinGuests ?? "",
        eventMaxGuests: areaData.eventMaxGuests ?? "",
        imageUrl: areaData.imageUrl || null, // Preserve existing imageUrl in formData
      };
      
      setFormData(initializedData);
      setSelectedServiceTimes(areaData?.serviceTimes || []);
      
      // Initialize image state for display
      const baseURL = axiosInstance.defaults.baseURL || "";
      let imageUrl = "";
      if (areaData.imageUrl) {
        if (areaData.imageUrl.startsWith("http")) {
          imageUrl = areaData.imageUrl;
        } else {
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          const imagePath = areaData.imageUrl.startsWith("/") ? areaData.imageUrl : `/${areaData.imageUrl}`;
          imageUrl = `${baseUrlClean}${imagePath}`;
        }
      }
      setImageState({
        imageUrl: imageUrl,
        imageFile: null,
      });
    } catch (error) {
      // Error fetching dining area data silently
      showErrorToast("Failed to load dining area data");
    } finally {
      setLoading(false);
    }
  }, [CompID, sA_ID]);

  // Fetch advance deposit rules for this service area and convert to inline format
  const fetchAdvanceDepositRules = useCallback(async () => {
    if (!CompID || !sA_ID) {
      setRules([]);
      return;
    }
    
    try {
      setRulesLoading(true);
      const response = await axiosInstance.get(APIs.GET_ADVANCE_DEPOSIT_RULES, {
        params: {
          CompID,
          PageNumber: 1,
          PageSize: 1000,
          Search: null,
          SortBy: "Priority",
          SortOrder: "DESC",
          Status: null,
        },
      });

      const responseData = response.data.data || {};
      const rulesList = responseData.rules || responseData.Rules || [];
      
      // Filter rules by ServiceAreaID matching current service area
      const filteredRules = rulesList.filter(rule => rule.serviceAreaID === parseInt(sA_ID));
      
      // Convert to inline format with all details
      const formattedRules = await Promise.all(filteredRules.map(async (rule) => {
        // Fetch full rule details including weekly timings
        try {
          const detailResponse = await axiosInstance.get(APIs.GET_ADVANCE_DEPOSIT_RULE_BY_ID, {
            params: { CompID, DepositRuleID: rule.id },
          });
          const fullRule = detailResponse.data.data || rule;
          
          // Convert weekly timings to schedule format
          let ruleSchedules = schedule.map(s => ({ ...s, timeSlots: [] }));
          let ruleCommonTimeSlot = [{ from: "00:00", to: "23:59" }];
          let useSameTimeSlots = true;
          
          if (fullRule.weeklyTimings && fullRule.weeklyTimings.length > 0) {
            const timingsByDay = {};
            fullRule.weeklyTimings.forEach((timing) => {
              const dayName = timing.dayName;
              if (!timingsByDay[dayName]) {
                timingsByDay[dayName] = [];
              }
              timingsByDay[dayName].push({
                from: toStateTimeHHmm(timing.openTime),
                to: toStateTimeHHmm(timing.closeTime),
              });
            });
            
            // Check if all days have same slots
            const allDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
            const daySlots = allDays.map((day) => timingsByDay[day] || []);
            const firstNonEmpty = daySlots.find((slots) => slots.length > 0) || [];
            
            let allSame = firstNonEmpty.length > 0;
            if (allSame) {
              allSame = daySlots.every((slots) => {
                if (slots.length !== firstNonEmpty.length) return false;
                return slots.every((slot, idx) => {
                  const ref = firstNonEmpty[idx];
                  return slot.from === ref.from && slot.to === ref.to;
                });
              });
            }
            
            if (allSame && firstNonEmpty.length > 0) {
              useSameTimeSlots = true;
              ruleCommonTimeSlot = firstNonEmpty.map(s => ({ ...s }));
              ruleSchedules = ruleSchedules.map(s => ({
                ...s,
                timeSlots: firstNonEmpty.map(s => ({ ...s })),
              }));
            } else {
              useSameTimeSlots = false;
              ruleSchedules = ruleSchedules.map(s => ({
                ...s,
                timeSlots: timingsByDay[s.day] || [],
              }));
            }
          }
          
          return {
            id: rule.id,
            ruleName: rule.ruleName || "",
            depositType: rule.depositType || "FixedAmount",
            depositValue: rule.depositValue?.toString() || "",
            isActive: rule.isActive !== undefined ? rule.isActive : true,
            priority: rule.priority?.toString() || "0",
            enableDateRange: rule.isSetDateRange || false,
            dateRange: {
              startDateTime: rule.depositStartDate ? moment(rule.depositStartDate) : null,
              endDateTime: rule.depositEndDate ? moment(rule.depositEndDate) : null,
            },
            enableSchedule: fullRule.weeklyTimings && fullRule.weeklyTimings.length > 0,
            useSameTimeSlots: useSameTimeSlots,
            schedules: ruleSchedules,
            commonTimeSlot: ruleCommonTimeSlot,
            enableMaxDeposit: rule.isSetMaxDepositValue || false,
            maxDepositValue: rule.maxDepositValue?.toString() || "",
            enableMinDeposit: rule.isSetMinDepositValue || false,
            minDepositValue: rule.minDepositValue?.toString() || "",
            serviceAreaID: parseInt(sA_ID),
            _isUnsaved: false,
          };
        } catch (err) {
          // If detail fetch fails, return basic rule
          return {
            id: rule.id,
            ruleName: rule.ruleName || "",
            depositType: rule.depositType || "FixedAmount",
            depositValue: rule.depositValue?.toString() || "",
            isActive: rule.isActive !== undefined ? rule.isActive : true,
            priority: rule.priority?.toString() || "0",
            enableDateRange: rule.isSetDateRange || false,
            dateRange: {
              startDateTime: rule.depositStartDate ? moment(rule.depositStartDate) : null,
              endDateTime: rule.depositEndDate ? moment(rule.depositEndDate) : null,
            },
            enableSchedule: false,
            useSameTimeSlots: true,
            schedules: schedule.map(s => ({ ...s, timeSlots: [] })),
            commonTimeSlot: [{ from: "00:00", to: "23:59" }],
            enableMaxDeposit: rule.isSetMaxDepositValue || false,
            maxDepositValue: rule.maxDepositValue?.toString() || "",
            enableMinDeposit: rule.isSetMinDepositValue || false,
            minDepositValue: rule.minDepositValue?.toString() || "",
            serviceAreaID: parseInt(sA_ID),
            _isUnsaved: false,
          };
        }
      }));
      
      setRules(formattedRules);
      // Expand all rules by default when fetched
      if (formattedRules.length > 0) {
        setExpandedRules(new Set(formattedRules.map((_, index) => index)));
      }
      
      // Automatically enable applyRules if there are existing rules
      if (formattedRules.length > 0 && !applyRules) {
        setApplyRules(true);
      }
    } catch (err) {
      setRules([]);
      if (err.response?.status !== 404) {
        showErrorToast("Failed to load advance deposit rules.");
      }
    } finally {
      setRulesLoading(false);
    }
  }, [CompID, sA_ID]);

  // Get profile data for currency
  const { profileData } = useSelector((state) => state.auth);

  useEffect(() => {
    if (CompID) {
      fetchServicePeriods();
      fetchDiningAreaData();
    }
  }, [CompID, fetchServicePeriods, fetchDiningAreaData]);

  useEffect(() => {
    if (profileData?.compCurrency) {
      setCompCurrency(profileData.compCurrency);
    }
  }, [profileData?.compCurrency]);

  // Fetch rules when service area is loaded
  useEffect(() => {
    if (CompID && sA_ID) {
      fetchAdvanceDepositRules();
    }
  }, [CompID, sA_ID, fetchAdvanceDepositRules]);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      formData,
      selectedServiceTimes: (selectedServiceTimes || []).map((st) => ({
        id: st.id ?? st.sT_ID,
        sT_Name: st.sT_Name || "",
      })),
      imageUrl: imageState.imageUrl || "",
      imageFileName: imageState.imageFile?.name || "",
    });
  }, [formData, selectedServiceTimes, imageState.imageUrl, imageState.imageFile]);

  useEffect(() => {
    if (!loading && formData && initialLoadedDataSnapshot === null) {
      setInitialLoadedDataSnapshot(getFormSnapshot());
    }
  }, [loading, formData, getFormSnapshot, initialLoadedDataSnapshot]);

  const isDirty =
    initialLoadedDataSnapshot !== null &&
    getFormSnapshot() !== initialLoadedDataSnapshot;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTableChange = (index, field, value) => {
    const updatedTables = [...formData?.serviceAreaTables];
    updatedTables[index] = { ...updatedTables[index], [field]: value };
    setFormData((prev) => ({ ...prev, serviceAreaTables: updatedTables }));
  };

  const handleTableToggleStatus = (index) => {
    const updatedTables = [...formData?.serviceAreaTables];
    updatedTables[index] = {
      ...updatedTables[index],
      t_Publish: !updatedTables[index].t_Publish,
    };
    setFormData((prev) => ({ ...prev, serviceAreaTables: updatedTables }));
  };

  const addTableRow = () => {
    setFormData((prev) => ({
      ...prev,
      serviceAreaTables: [
        ...prev.serviceAreaTables,
        {
          t_ID: 0,
          localizeID: "en",
          t_Name: "",
          t_NoOfSeats: "",
          t_Shape: "",
          t_Status: "Available",
          t_Publish: true,
          t_Xaxis: 0,
          t_Yaxis: 0,
          t_Remarks: "",
          t_Version: 1,
          updatedateTime: new Date().toISOString(),
          compID: CompID,
          sA_ID: sA_ID,
          isTableMerged: false,
          mergedTableID: null,
          angel: 0, // Default angle for new tables
          t_OriginalStatus: "Available",
        },
      ],
    }));
  };

  const removeTableRow = async (table, index) => {
    if (formData?.serviceAreaTables.length === 1) {
      showErrorToast("At least one table is required");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${
          table.t_Name || "this table"
        }?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      if (table.t_ID > 0) {
        const response = await axiosInstance.delete(
          "/api/ServiceAreaTable/DeleteServiceAreaTable",
          {
            params: {
              ID: table.t_ID,
            },
          }
        );

        if (response.data.status) {
          showSuccessToast("Table deleted successfully");
          setFormData((prev) => ({
            ...prev,
            serviceAreaTables: prev.serviceAreaTables.filter(
              (_, i) => i !== index
            ),
          }));
        } else {
          showErrorToast(response.data.message || "Deletion failed");
        }
      } else {
        // Remove locally for new tables (t_ID: 0)
        setFormData((prev) => ({
          ...prev,
          serviceAreaTables: prev.serviceAreaTables.filter(
            (_, i) => i !== index
          ),
        }));
        showSuccessToast("Table removed successfully");
      }
    } catch (err) {
      // Error deleting table silently
      showErrorToast("Failed to delete table");
    } finally {
      setLoading(false);
    }
  };

  const handleServiceTimeSubmit = (selectedTimes) => {
    setSelectedServiceTimes(selectedTimes);
    setIsServiceTimeModalOpen(false);
  };

  const handleRemoveServiceTime = (serviceTime) => {
    setSelectedServiceTimes((prev) =>
      prev.filter((item) => item.id !== serviceTime.id)
    );
  };

  const handleImageChange = (url, file) => {
    setImageState({
      imageUrl: url,
      imageFile: file,
    });
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    try {
      if (!formData?.sA_Name || formData?.sA_Name.trim().length === 0) {
        showErrorToast("Please enter a valid dining area name");
        return false;
      }

      if (selectedServiceTimes.length === 0) {
        showErrorToast("Please select at least one service time");
        return false;
      }

      const validTables = formData?.serviceAreaTables.filter(
        (table) =>
          table.t_Name?.trim().length > 0 &&
          table.t_NoOfSeats &&
          !isNaN(table.t_NoOfSeats) &&
          parseInt(table.t_NoOfSeats) > 0
      );

      if (validTables.length === 0) {
        showErrorToast("At least one valid table is required");
        return false;
      }

      // Validate private reservation capacity if enabled
      if (formData?.isPrivateReservationCapable) {
        if (formData.privateMinGuests && formData.privateMaxGuests) {
          const minGuests = parseInt(formData.privateMinGuests);
          const maxGuests = parseInt(formData.privateMaxGuests);
          if (minGuests >= maxGuests) {
            showErrorToast("Private max guests must be greater than min guests");
            return false;
          }
          if (minGuests < 1 || maxGuests < 1) {
            showErrorToast("Private guest limits must be at least 1");
            return false;
          }
        }
      }

      // Validate event reservation capacity if enabled
      if (formData?.isEventCapable) {
        if (formData.eventMinGuests && formData.eventMaxGuests) {
          const minGuests = parseInt(formData.eventMinGuests);
          const maxGuests = parseInt(formData.eventMaxGuests);
          if (minGuests >= maxGuests) {
            showErrorToast("Event max guests must be greater than min guests");
            return false;
          }
          if (minGuests < 1 || maxGuests < 1) {
            showErrorToast("Event guest limits must be at least 1");
            return false;
          }
        }
      }

      for (const table of validTables) {
        if (!table.t_Name || table.t_Name.trim().length === 0) {
          showErrorToast("Please enter a valid table name for all tables");
          return false;
        }
        if (
          !table.t_NoOfSeats ||
          isNaN(table.t_NoOfSeats) ||
          parseInt(table.t_NoOfSeats) <= 0
        ) {
          showErrorToast("Please enter a valid number of seats for all tables");
          return false;
        }
      }

      // Save only unsaved advance deposit rules first (if rules are enabled)
      if (applyRules && Array.isArray(rules) && rules.length > 0) {
        const unsavedRuleIndexes = rules
          .map((rule, index) => ({ rule, index }))
          .filter(({ rule }) => rule && (rule.id === 0 || rule._isUnsaved === true))
          .map(({ index }) => index);

        for (const ruleIndex of unsavedRuleIndexes) {
          const saved = await saveRule(ruleIndex, { silent: true, skipRefresh: true });
          if (!saved) {
            showErrorToast("Please fix and save advance deposit rules before updating dining area.");
            return false;
          }
        }
      }

      setLoading(true);
      const now = new Date().toISOString();

      // Upload image first if a new image file is provided
      // Preserve existing imageUrl if no new image is uploaded
      let finalImageUrl = formData?.imageUrl || null;
      const baseURL = axiosInstance.defaults.baseURL || "";

      if (imageState.imageFile) {
        try {
          const imageFormData = new FormData();
          imageFormData.append("ImageFile", imageState.imageFile);

          const imageResponse = await axiosInstance.post(
            "/api/ServiceArea/UploadServiceAreaImage",
            imageFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          if (imageResponse.data?.status && imageResponse.data?.data?.filePath) {
            finalImageUrl = imageResponse.data.data.filePath;
          }
        } catch (error) {
          showErrorToast("Failed to upload image. Please try again.");
          setLoading(false);
          return false;
        }
      }
      // If no new image uploaded and no existing imageUrl, keep it as null
      // This ensures we preserve existing images when updating other fields

      // Calculate number of tables and total seats
      const numberOfTables = validTables.length;
      const numberOfSeats = validTables.reduce((total, table) => {
        return total + (parseInt(table.t_NoOfSeats) || 0);
      }, 0);

      const payload = {
        CompID,
        ID: parseInt(sA_ID),
        sA_Name: formData?.sA_Name,
        sA_Detail: formData?.sA_Detail,
        imageUrl: finalImageUrl,
        sA_Status: formData?.sA_Status ?? false,
        sA_Width: formData?.sA_Width,
        sA_Height: formData?.sA_Height,
        sA_SortOrder: formData?.sA_SortOrder,
        updateDateTime: now,
        NumberofTables: numberOfTables,
        NumberofSeats: numberOfSeats,
        // Always enable regular reservations (table-based)
        isRegularReservationCapable: true,
        isPrivateReservationCapable: formData?.isPrivateReservationCapable ?? false,
        isEventCapable: formData?.isEventCapable ?? false,
        privateMinGuests: formData?.privateMinGuests ? parseInt(formData.privateMinGuests) : null,
        privateMaxGuests: formData?.privateMaxGuests ? parseInt(formData.privateMaxGuests) : null,
        eventMinGuests: formData?.eventMinGuests ? parseInt(formData.eventMinGuests) : null,
        eventMaxGuests: formData?.eventMaxGuests ? parseInt(formData.eventMaxGuests) : null,
        ServiceAreaAndTimeRelations: selectedServiceTimes?.map((item) => ({
          id: 0,
          sA_ID: parseInt(sA_ID),
          sT_ID: Number(item.id),
          compID: CompID,
          addUpdateTime: now,
        })),
        serviceAreaTables: validTables.map((table) => ({
          ...table,
          angel: table.angel || 0, 
        })),
      };

      const response = await axiosInstance.put(
        "/api/ServiceArea/UpdateServiceArea",
        payload
      );

      if (response.data.status) {
        showSuccessToast("Dining area updated successfully");
        setInitialLoadedDataSnapshot(getFormSnapshot());
        if (exitAfterSave) {
          navigate("/diningAreas");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to update dining area");
        return false;
      }
    } catch (error) {
      // Update failed silently
      showErrorToast("Failed to update dining area");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getSelectedServiceTimesText = () => {
    return selectedServiceTimes.map((st) => st.sT_Name).join(", ");
  };

  // Advance Deposit Rules handlers - Inline editing
  const addRuleRow = () => {
    const newRule = {
      id: 0, // 0 means new rule
      ruleName: "",
      depositType: "FixedAmount",
      depositValue: "",
      isActive: true,
      priority: "0",
      enableDateRange: false,
      dateRange: {
        startDateTime: null,
        endDateTime: null,
      },
      enableSchedule: false,
      useSameTimeSlots: true,
      schedules: schedule.map(s => ({ ...s, timeSlots: [] })),
      commonTimeSlot: [{ from: "00:00", to: "23:59" }],
      enableMaxDeposit: false,
      maxDepositValue: "",
      enableMinDeposit: false,
      minDepositValue: "",
      serviceAreaID: parseInt(sA_ID),
      _isUnsaved: true,
    };
    setRules((prev) => {
      const newRules = [...prev, newRule];
      // Expand the new rule automatically
      setExpandedRules((prevExpanded) => {
        const newExpanded = new Set(prevExpanded);
        newExpanded.add(newRules.length - 1);
        return newExpanded;
      });
      return newRules;
    });
  };

  const handleRuleChange = (index, field, value) => {
    const updatedRules = [...rules];
    if (field.includes('.')) {
      // Handle nested fields like dateRange.startDateTime
      const [parent, child] = field.split('.');
        updatedRules[index] = {
        ...updatedRules[index],
        [parent]: {
          ...(updatedRules[index][parent] || {}),
          [child]: value,
        },
          _isUnsaved: true,
      };
    } else {
      // If setting the entire dateRange object, ensure it's properly merged
      if (field === 'dateRange' && typeof value === 'object' && value !== null) {
        updatedRules[index] = {
          ...updatedRules[index],
          [field]: {
            ...(updatedRules[index][field] || {}),
            ...value,
          },
            _isUnsaved: true,
        };
      } else {
          updatedRules[index] = { ...updatedRules[index], [field]: value, _isUnsaved: true };
      }
    }
    setRules(updatedRules);
  };

  const applyCommonTimeSlotToAllForRule = (index) => {
    setRules((prevRules) => {
      const updatedRules = [...prevRules];
      const rule = updatedRules[index];
      if (!rule) return prevRules;

      const sourceSlots = Array.isArray(rule.commonTimeSlot) ? rule.commonTimeSlot : [];
      const updatedSchedules = (rule.schedules || []).map((schedule) => ({
        ...schedule,
        timeSlots: sourceSlots.map((slot) => ({ ...slot })),
      }));

      updatedRules[index] = {
        ...rule,
        useSameTimeSlots: true,
        schedules: updatedSchedules,
        _isUnsaved: true,
      };
      return updatedRules;
    });
  };

  const saveRule = async (index, options = {}) => {
    const { silent = false, skipRefresh = false } = options;
    const rule = rules[index];
    
    if (!rule.ruleName.trim()) {
      showErrorToast("Rule name is required");
      return;
    }

    if (rule.depositValue === undefined || rule.depositValue === null || rule.depositValue === "" || isNaN(parseFloat(rule.depositValue))) {
      showErrorToast("Valid deposit value is required");
      return;
    }

    if (parseFloat(rule.depositValue) < 0) {
      showErrorToast("Deposit value cannot be negative");
      return;
    }

    if (rule.enableDateRange) {
      if (!rule.dateRange.startDateTime || !moment(rule.dateRange.startDateTime).isValid()) {
        showErrorToast("Please select a valid start date");
        return;
      }
      if (!rule.dateRange.endDateTime || !moment(rule.dateRange.endDateTime).isValid()) {
        showErrorToast("Please select a valid end date");
        return;
      }
      if (moment(rule.dateRange.endDateTime).isBefore(rule.dateRange.startDateTime)) {
        showErrorToast("End date cannot be before start date");
        return;
      }
    }

    if (rule.enableMaxDeposit && (!rule.maxDepositValue || parseFloat(rule.maxDepositValue) <= 0)) {
      showErrorToast("Valid maximum deposit value is required when enabled");
      return;
    }

    if (rule.enableMinDeposit && (!rule.minDepositValue || parseFloat(rule.minDepositValue) <= 0)) {
      showErrorToast("Valid minimum deposit value is required when enabled");
      return;
    }

    setRulesLoading(true);

    try {
      let depositStartDate = null;
      let depositEndDate = null;
      if (rule.enableDateRange) {
        if (rule.dateRange.startDateTime && moment(rule.dateRange.startDateTime).isValid()) {
          depositStartDate = moment(rule.dateRange.startDateTime).format("YYYY-MM-DD");
        }
        if (rule.dateRange.endDateTime && moment(rule.dateRange.endDateTime).isValid()) {
          depositEndDate = moment(rule.dateRange.endDateTime).format("YYYY-MM-DD");
        }
      }

      const formattedSchedules = [];
      if (rule.enableSchedule) {
        if (rule.useSameTimeSlots) {
          const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
          days.forEach((day) => {
            rule.commonTimeSlot.forEach((slot) => {
              formattedSchedules.push({
                dayName: day,
                openTime: toPayloadTime12Hour(slot.from),
                closeTime: toPayloadTime12Hour(slot.to),
              });
            });
          });
        } else {
          rule.schedules.forEach((schedule) => {
            schedule.timeSlots.forEach((slot) => {
              formattedSchedules.push({
                dayName: schedule.day,
                openTime: toPayloadTime12Hour(slot.from),
                closeTime: toPayloadTime12Hour(slot.to),
              });
            });
          });
        }
      }

      const payload = {
        id: rule.id || 0,
        compID: CompID,
        ruleName: rule.ruleName,
        isActive: rule.isActive,
        isSetDateRange: rule.enableDateRange,
        depositStartDate: depositStartDate,
        depositEndDate: depositEndDate,
        depositType: rule.depositType,
        depositValue: parseFloat(rule.depositValue),
        isSetMaxDepositValue: rule.enableMaxDeposit,
        maxDepositValue: rule.enableMaxDeposit ? parseFloat(rule.maxDepositValue) : null,
        isSetMinDepositValue: rule.enableMinDeposit,
        minDepositValue: rule.enableMinDeposit ? parseFloat(rule.minDepositValue) : null,
        priority: parseInt(rule.priority) || 0,
        serviceAreaID: parseInt(sA_ID), // Automatically set to current service area
        weeklyTimings: formattedSchedules,
      };

      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_ADVANCE_DEPOSIT_RULE,
        payload
      );

      if (response.data.status) {
        if (!silent) {
          showSuccessToast(rule.id === 0 ? "Rule added successfully" : "Rule updated successfully");
        }

        const savedRule = response.data.data || {};
        const savedRuleId = savedRule.id || savedRule.ID || rule.id || 0;

        setRules((prev) =>
          prev.map((r, i) =>
            i === index
              ? {
                  ...r,
                  id: savedRuleId,
                  _isUnsaved: false,
                }
              : r
          )
        );

        // Refresh rules to get updated data unless explicitly skipped
        if (!skipRefresh) {
          await fetchAdvanceDepositRules();
        }
        return true;
      } else {
        if (!silent) {
          showErrorToast(response.data.message || "Failed to save rule");
        }
        return false;
      }
    } catch (err) {
      if (!silent) {
        showErrorToast("Error saving rule");
      }
      return false;
    } finally {
      setRulesLoading(false);
    }
  };

  const removeRuleRow = async (rule, index) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete rule "${rule.ruleName || "this rule"}"?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (rule.id > 0) {
        // Existing rule - delete from database
        setRulesLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_ADVANCE_DEPOSIT_RULE, {
          params: {
            CompID,
            DepositRuleID: rule.id,
          },
        });

        if (response.data.status) {
          showSuccessToast("Rule deleted successfully");
          setRules((prev) => prev.filter((_, i) => i !== index));
        } else {
          showErrorToast(response.data.message || "Deletion failed");
        }
        setRulesLoading(false);
      } else {
        // New rule - just remove from local state
        setRules((prev) => prev.filter((_, i) => i !== index));
      }
    } catch (err) {
      showErrorToast("Failed to delete rule");
      setRulesLoading(false);
    }
  };

  const formatDepositValue = (rule) => {
    const symbol = getCurrencySymbol(compCurrency);
    
    switch (rule.depositType) {
      case "FixedAmount":
        return `${symbol}${parseFloat(rule.depositValue || 0).toFixed(2)}`;
      case "Percentage":
        return `${rule.depositValue || 0}%`;
      case "PerPerson":
        return `${symbol}${parseFloat(rule.depositValue || 0).toFixed(2)} per person`;
      default:
        return `${symbol}${parseFloat(rule.depositValue || 0).toFixed(2)}`;
    }
  };

  useUnsavedChangesGuard({
    isDirty: !loading && isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => setInitialLoadedDataSnapshot(getFormSnapshot()),
  });

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/dining-areas">Dining Areas</Link>
            </li>
            <li className="breadcrumb-item active">Update Dining Area</li>
          </ol>
          <h1 className="page-header mb-0">Update Dining Area</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/diningAreas")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
          disabled={!formData?.sA_Name?.trim()}
          savingLabel="Updating..."
        />
      </div>
      <GridSectionLoader loading={loading} minHeight="320px">
      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Update Dining Area</PanelHeader>
          <PanelBody>
            <div className="p-2">
              <div className="row">
                <div className="col-md-7">
                  <div className="col-md-12">
                    <FormInput
                      label="Dining Area Name"
                      placeHolder="Enter dining area name"
                      type="text"
                      name="sA_Name"
                      value={formData?.sA_Name}
                      onchange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Service Time *</label>
                    <div className="position-relative">
                      <div
                        className="form-control d-flex align-items-center justify-content-between"
                        style={{
                          cursor: "pointer",
                          minHeight: "38px",
                          backgroundColor:
                            selectedServiceTimes.length > 0
                              ? (isDarkMode ? "#111827" : "#ffffff")
                              : (isDarkMode ? "#1f2937" : "#f8f9fa"),
                          border:
                            selectedServiceTimes.length > 0
                              ? `1px solid ${themeColor}`
                              : `1px solid ${isDarkMode ? "#374151" : "#ced4da"}`,
                          color: isDarkMode ? "#e5e7eb" : "inherit",
                          borderRadius: "8px",
                          boxShadow: isDarkMode ? "0 1px 2px rgba(0,0,0,0.4)" : "none"
                        }}
                        onClick={() => setIsServiceTimeModalOpen(true)}
                      >
                        <div className="d-flex flex-wrap gap-1">
                          {selectedServiceTimes.length > 0 ? (
                            selectedServiceTimes.map((serviceTime) => (
                              <span
                                key={serviceTime.id}
                                className="badge me-1 mb-1 d-flex align-items-center"
                                style={{
                                  fontSize: "0.75rem",
                                  backgroundColor: themeColor,
                                  color: "#ffffff",
                                  borderRadius: "12px",
                                  padding: "6px 10px",
                                }}
                              >
                                {serviceTime.sT_Name}
                                <i
                                  className="fa fa-times ms-1"
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveServiceTime(serviceTime);
                                  }}
                                ></i>
                              </span>
                            ))
                          ) : (
                            <span className={isDarkMode ? "text-gray-400" : "text-muted"}>Select service times</span>
                          )}
                        </div>
                        {selectedServiceTimes.length > 0 && (
                          <i
                            className="fa fa-times-circle"
                            style={{
                              cursor: "pointer",
                              color: isDarkMode ? "#f87171" : "#dc3545",
                              fontSize: "16px",
                              marginLeft: "8px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedServiceTimes([]);
                            }}
                            title="Clear all selections"
                          ></i>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row d-none">
                    <div className="col-6 mb-3">
                      <FormInput
                        label="Dining Area Width(px)"
                        placeHolder="Enter dining area Width"
                        type="number"
                        name="sA_Width"
                        step="0.01"
                        value={formData?.sA_Width || 0}
                        onchange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-6 mb-3">
                      <FormInput
                        label="Dining Area Height(px)"
                        placeHolder="Enter dining area Height"
                        type="number"
                        name="sA_Height"
                        step="0.01"
                        value={formData?.sA_Height || 0}
                        onchange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <div className="service-area-rich-editor-wrap">
                      <ReactQuill
                        theme="snow"
                        value={formData?.sA_Detail || ""}
                        onChange={(value) =>
                          setFormData((prev) => ({ ...prev, sA_Detail: value }))
                        }
                        modules={detailEditorModules}
                        placeholder="Enter detailed description..."
                        className="service-area-rich-editor"
                      />
                    </div>
                   
                  </div>
                </div>
                <div className="col-md-5 mb-3">
                  <label className="form-label">Service Area Image</label>
                  <p className="text-muted mb-2" style={{ fontSize: '0.875rem', color: isDarkMode ? '#9ca3af' : '#6c757d' }}>
                    Upload an image to showcase this service area. This will be displayed to customers when making reservations.
                  </p>
                  <ImageUpload
                    imageUrl={imageState.imageUrl}
                    setImage={handleImageChange}
                    imageFile={imageState.imageFile}
                    height="250px"
                    width="100%"
                    uploadText="Upload Service Area Image"
                    buttonText="Change Image"
                    centerButton={true}
                    useGrid={false}
                    className="mb-3"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label">Status</label>
                  <div className="d-flex align-items-center">
                    <ToggleSwitch
                      isChecked={formData?.sA_Status ?? false}
                      onToggle={() =>
                        setFormData((prev) => ({
                          ...prev,
                          sA_Status: !(prev?.sA_Status ?? false),
                        }))
                      }
                      label={formData?.sA_Status ? "On" : "Off"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                </div>
              </div>

              {/* Reservation Capabilities UI hidden as per requirements.
                  Regular reservations (table-based) are always enabled in the payload.
                  Private/Event capability can still be controlled via backend or future UI if needed. */}
              <div className="row">
                <div className="col-12 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">Tables</label>
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        title="Print QR codes for all published tables"
                        onClick={() => {
                          const hasPublished = formData?.serviceAreaTables?.some(
                            (t) => t.t_ID > 0 && t.t_Publish
                          );
                          if (!hasPublished) {
                            showErrorToast(
                              "Publish at least one saved table to print QR codes"
                            );
                            return;
                          }
                          setQrTableId(null);
                          setQrBulkMode(true);
                          setQrModalOpen(true);
                        }}
                      >
                        <i className="fa fa-qrcode me-1"></i>
                        Print table QRs
                      </button>
                      <span className="badge bg-info">
                        <i className="fa fa-table me-1"></i>
                        Tables: {formData?.serviceAreaTables?.filter(table => 
                          table.t_Name?.trim().length > 0 && 
                          table.t_NoOfSeats && 
                          !isNaN(table.t_NoOfSeats) && 
                          parseInt(table.t_NoOfSeats) > 0
                        ).length || 0}
                      </span>
                      <span className="badge bg-success">
                        <i className="fa fa-users me-1"></i>
                        Seats: {formData?.serviceAreaTables?.reduce((total, table) => {
                          return total + (parseInt(table.t_NoOfSeats) || 0);
                        }, 0) || 0}
                      </span>
                    </div>
                  </div>
                  <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Table Name</th>
                      <th>Number of Seats</th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Shape
                      </th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Status
                      </th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Publish
                      </th>
                      <th
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData?.serviceAreaTables.map((table, index) => (
                      <tr key={table.t_ID || `draft-${index}`}>
                        {(() => {
                          const isEditable = table.t_OriginalStatus === "Available";
                          return (
                            <>
                              <td>
                                <FormInput
                                  placeHolder="Enter table name"
                                  type="text"
                                  value={table.t_Name}
                                  onchange={(e) =>
                                    handleTableChange(
                                      index,
                                      "t_Name",
                                      e.target.value
                                    )
                                  }
                                  required
                                  readOnly={!isEditable}
                                />
                              </td>
                              <td>
                                <FormInput
                                  placeHolder="Enter number of seats"
                                  type="number"
                                  value={table.t_NoOfSeats}
                                  onchange={(e) =>
                                    handleTableChange(
                                      index,
                                      "t_NoOfSeats",
                                      e.target.value
                                    )
                                  }
                                  required
                                  step="1"
                                  min="1"
                                  readOnly={!isEditable}
                                />
                              </td>
                              <td>
                                <div className="d-flex justify-content-center gap-2">
                                  {["Square", "Rectangle", "Round"].map((shape) => (
                                    <button
                                      key={shape}
                                      type="button"
                                      className={`btn btn-sm ${table.t_Shape === shape
                                          ? "btn-primary"
                                          : "btn-outline-secondary"
                                        }`}
                                      onClick={() => handleTableChange(index, "t_Shape", shape)}
                                      disabled={!isEditable}
                                      style={{
                                        width: "60px",
                                        height: "40px",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "12px",
                                        fontWeight: "500"
                                      }}
                                      title={shape}
                                    >
                                      {shape === "Square" && (
                                        <div
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            border: "1px solid",
                                            borderColor: table.t_Shape === shape ? "#fff" : "#6c757d"
                                          }}
                                        />
                                      )}
                                      {shape === "Rectangle" && (
                                        <div
                                          style={{
                                            width: "24px",
                                            height: "16px",
                                            backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            border: "1px solid",
                                            borderColor: table.t_Shape === shape ? "#fff" : "#6c757d"
                                          }}
                                        />
                                      )}
                                      {shape === "Round" && (
                                        <div
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            border: "1px solid",
                                            borderColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                            borderRadius: "50%"
                                          }}
                                        />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <select
                                  className="form-control"
                                  disabled={!isEditable}
                                  style={{
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                  }}
                                  value={table.t_Status || ""}
                                  onChange={(e) =>
                                    handleTableChange(
                                      index,
                                      "t_Status",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select Status</option>
                                  <option value="Available">Available</option>
                                  <option value="Dirty">Dirty</option>
                                  <option value="Seated">Seated</option>
                                  <option value="Reserved">Reserved</option>
                                  <option value="Blocked">Blocked</option>
                                </select>
                              </td>
                              <td className="d-flex justify-content-center">
                                <ToggleSwitch
                                  isChecked={table.t_Publish}
                                  onToggle={() =>
                                    handleTableToggleStatus(index)
                                  }
                                  label={
                                    table.t_Publish ? "Active" : "Inactive"
                                  }
                                  disabled={!isEditable}
                                  themeColor={themeColor}
                                />
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                {table.t_ID > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm me-1"
                                    title={
                                      table.t_Publish
                                        ? "Generate QR for online ordering"
                                        : "Publish table to generate QR"
                                    }
                                    disabled={!table.t_Publish}
                                    onClick={() => {
                                      if (!table.t_Publish) {
                                        showErrorToast(
                                          "Publish table to generate QR"
                                        );
                                        return;
                                      }
                                      setQrBulkMode(false);
                                      setQrTableId(table.t_ID);
                                      setQrModalOpen(true);
                                    }}
                                  >
                                    <i className="fa fa-qrcode"></i>
                                  </button>
                                )}
                                {index ===
                                formData?.serviceAreaTables.length - 1 ? (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-success btn-sm"
                                      onClick={addTableRow}
                                    >
                                      <i className="fa fa-plus"></i>
                                    </button>{" "}
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() =>
                                        removeTableRow(table, index)
                                      }
                                      disabled={!isEditable}
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => removeTableRow(table, index)}
                                    disabled={!isEditable}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                )}
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              </div>

              {/* Advance Deposit Rules Section */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h5 className="card-title mb-1" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                        Advance Deposit Rules
                      </h5>
                      <p className="text-muted mb-0" style={{ fontSize: '0.875rem', color: isDarkMode ? '#9ca3af' : '#6c757d' }}>
                        Create and manage advance deposit rules for this service area
                      </p>
                    </div>
                    <ToggleSwitch
                      isChecked={applyRules}
                      onToggle={setApplyRules}
                      label={applyRules ? "Rules Enabled" : "Rules Disabled"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>

                  {applyRules && (
                    <div className="card border-0 shadow-sm" style={{ 
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}>
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <div className="d-flex align-items-center gap-3">
                            <div 
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 4px 12px ${themeColor}30`
                              }}
                            >
                              <i className="fa fa-credit-card text-white" style={{ fontSize: '20px' }}></i>
                            </div>
                            <div>
                              <h5 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                                Advance Deposit Rules
                              </h5>
                              <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                                {rules.length} {rules.length === 1 ? 'Rule' : 'Rules'} configured
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn"
                            onClick={addRuleRow}
                            disabled={rulesLoading}
                            style={{
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '10px 20px',
                              fontWeight: '600',
                              boxShadow: `0 4px 12px ${themeColor}30`,
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = `0 6px 16px ${themeColor}40`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}30`;
                            }}
                          >
                            <i className="fa fa-plus me-2"></i>
                            Add New Rule
                          </button>
                        </div>

                        {rulesLoading && rules.length === 0 ? (
                          <GridSectionLoader loading={true} minHeight="220px" />
                        ) : rules.length === 0 ? (
                          <div className="text-center py-5" style={{
                            backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                            borderRadius: '12px',
                            border: `2px dashed ${isDarkMode ? '#404040' : '#dee2e6'}`
                          }}>
                            <div 
                              style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                background: isDarkMode ? '#374151' : '#e9ecef',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px'
                              }}
                            >
                              <i className="fa fa-credit-card" style={{ 
                                fontSize: '32px', 
                                color: isDarkMode ? '#9ca3af' : '#6c757d' 
                              }}></i>
                            </div>
                            <h6 style={{ color: isDarkMode ? '#ffffff' : '#000000', marginBottom: '8px' }}>
                              No Rules Configured
                            </h6>
                            <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                              Create your first advance deposit rule to get started
                            </p>
                            <button
                              type="button"
                              className="btn"
                              onClick={addRuleRow}
                              style={{
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '10px 24px',
                                fontWeight: '600',
                                boxShadow: `0 4px 12px ${themeColor}30`
                              }}
                            >
                              <i className="fa fa-plus me-2"></i>
                              Create First Rule
                            </button>
                          </div>
                        ) : (
                          <div className="rules-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {rules.map((rule, index) => (
                              <div
                                key={rule.id || `new-${index}`}
                                className="card"
                                style={{
                                  backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                  border: `2px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
                                  borderRadius: '16px',
                                  boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                                  transition: 'all 0.3s ease',
                                  overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = themeColor;
                                  e.currentTarget.style.boxShadow = isDarkMode 
                                    ? `0 6px 20px ${themeColor}40` 
                                    : `0 4px 16px ${themeColor}30`;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = isDarkMode ? '#404040' : '#e9ecef';
                                  e.currentTarget.style.boxShadow = isDarkMode 
                                    ? '0 4px 12px rgba(0,0,0,0.3)' 
                                    : '0 2px 8px rgba(0,0,0,0.08)';
                                }}
                              >
                                {/* Rule Header */}
                                <div 
                                  style={{
                                    background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}08 100%)`,
                                    padding: '20px 24px',
                                    borderBottom: '1px solid ' + (isDarkMode ? '#404040' : '#e9ecef'),
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    const newExpanded = new Set(expandedRules);
                                    if (newExpanded.has(index)) {
                                      newExpanded.delete(index);
                                    } else {
                                      newExpanded.add(index);
                                    }
                                    setExpandedRules(newExpanded);
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
                                      <button
                                        type="button"
                                        className="btn btn-sm"
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          color: isDarkMode ? '#ffffff' : '#000000',
                                          padding: '4px 8px',
                                          minWidth: 'auto'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newExpanded = new Set(expandedRules);
                                          if (newExpanded.has(index)) {
                                            newExpanded.delete(index);
                                          } else {
                                            newExpanded.add(index);
                                          }
                                          setExpandedRules(newExpanded);
                                        }}
                                      >
                                        <i 
                                          className={`fa fa-chevron-${expandedRules.has(index) ? 'up' : 'down'}`}
                                          style={{
                                            transition: 'transform 0.3s ease',
                                            fontSize: '14px'
                                          }}
                                        ></i>
                                      </button>
                                      <div 
                                        style={{
                                          width: '40px',
                                          height: '40px',
                                          borderRadius: '10px',
                                          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          boxShadow: `0 2px 8px ${themeColor}30`
                                        }}
                                      >
                                        <i className="fa fa-gavel text-white"></i>
                                      </div>
                                      <div>
                                        <h6 className="mb-1" style={{ 
                                          color: isDarkMode ? '#ffffff' : '#000000',
                                          fontWeight: '600',
                                          fontSize: '1.1rem'
                                        }}>
                                          {rule.ruleName || `New Rule ${index + 1}`}
                                        </h6>
                                        <div className="d-flex align-items-center gap-2">
                                          <span 
                                            className="badge" 
                                            style={{
                                              backgroundColor: rule.isActive ? '#28a745' : '#6c757d',
                                              color: 'white',
                                              padding: '4px 10px',
                                              borderRadius: '6px',
                                              fontSize: '0.75rem',
                                              fontWeight: '500'
                                            }}
                                          >
                                            <i className={`fa fa-${rule.isActive ? 'check-circle' : 'pause-circle'} me-1`}></i>
                                            {rule.isActive ? 'Active' : 'Inactive'}
                                          </span>
                                          {rule.id > 0 && (
                                            <span 
                                              className="badge" 
                                              style={{
                                                backgroundColor: '#17a2b8',
                                                color: 'white',
                                                padding: '4px 10px',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                              }}
                                            >
                                              <i className="fa fa-sort-numeric-up me-1"></i>
                                              Priority: {rule.priority || 0}
                                            </span>
                                          )}
                                          {rule.id === 0 && (
                                            <span 
                                              className="badge" 
                                              style={{
                                                backgroundColor: '#ffc107',
                                                color: '#000',
                                                padding: '4px 10px',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                              }}
                                            >
                                              <i className="fa fa-edit me-1"></i>
                                              New
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                      {rule.id > 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-sm"
                                          onClick={() => saveRule(index)}
                                          disabled={rulesLoading}
                                          style={{
                                            background: '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            fontWeight: '500',
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!rulesLoading) {
                                              e.target.style.background = '#218838';
                                              e.target.style.transform = 'translateY(-1px)';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!rulesLoading) {
                                              e.target.style.background = '#28a745';
                                              e.target.style.transform = 'translateY(0)';
                                            }
                                          }}
                                        >
                                          <i className="fa fa-save me-1"></i>
                                          Save Changes
                                        </button>
                                      )}
                                      {rule.id === 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-sm"
                                          onClick={() => saveRule(index)}
                                          disabled={rulesLoading}
                                          style={{
                                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            fontWeight: '500',
                                            boxShadow: `0 2px 8px ${themeColor}30`,
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!rulesLoading) {
                                              e.target.style.transform = 'translateY(-1px)';
                                              e.target.style.boxShadow = `0 4px 12px ${themeColor}40`;
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!rulesLoading) {
                                              e.target.style.transform = 'translateY(0)';
                                              e.target.style.boxShadow = `0 2px 8px ${themeColor}30`;
                                            }
                                          }}
                                        >
                                          <i className="fa fa-check me-1"></i>
                                          Create Rule
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="btn btn-sm"
                                        onClick={() => removeRuleRow(rule, index)}
                                        disabled={rulesLoading}
                                        style={{
                                          background: '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '8px',
                                          padding: '8px 12px',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!rulesLoading) {
                                            e.target.style.background = '#c82333';
                                            e.target.style.transform = 'translateY(-1px)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!rulesLoading) {
                                            e.target.style.background = '#dc3545';
                                            e.target.style.transform = 'translateY(0)';
                                          }
                                        }}
                                      >
                                        <i className="fa fa-trash"></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <div 
                                  className="card-body p-4"
                                  style={{
                                    display: expandedRules.has(index) ? 'block' : 'none',
                                    transition: 'all 0.3s ease',
                                    overflow: 'hidden'
                                  }}
                                >

                                  {/* Rule Form Fields */}
                                  {/* Basic Information Section */}
                                  <div 
                                    className="mb-4"
                                    style={{
                                      padding: '20px',
                                      backgroundColor: isDarkMode ? '#374151' : '#f8f9fa',
                                      borderRadius: '12px',
                                      border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                                    }}
                                  >
                                    <div className="d-flex align-items-center mb-3">
                                      <i className="fa fa-info-circle me-2" style={{ color: themeColor }}></i>
                                      <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                                        Basic Information
                                      </h6>
                                    </div>
                                    <div className="row g-3">
                                      <div className="col-md-6">
                                        <FormInput
                                          label="Rule Name *"
                                          placeHolder="Enter rule name"
                                          type="text"
                                          value={rule.ruleName || ""}
                                          onchange={(e) => handleRuleChange(index, "ruleName", e.target.value)}
                                        />
                                      </div>
                                      <div className="col-md-6">
                                        <label className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>
                                          Deposit Type *
                                        </label>
                                        <Select
                                          options={depositTypeOptions}
                                          value={depositTypeOptions.find(opt => opt.value === rule.depositType)}
                                          onChange={(selected) =>
                                            handleRuleChange(index, "depositType", selected.value)
                                          }
                                          styles={dropdownStyles}
                                        />
                                      </div>
                                      <div className="col-md-6">
                                        <FormInput
                                          label={`Deposit Value * (${getCurrencySymbol(compCurrency)})`}
                                          placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                                          type="number"
                                          value={rule.depositValue || ""}
                                          onchange={(e) => handleRuleChange(index, "depositValue", e.target.value)}
                                          step="0.01"
                                          min={0}
                                        />
                                      </div>
                                      <div className="col-md-6">
                                        <FormInput
                                          label="Priority"
                                          placeHolder="0"
                                          type="number"
                                          value={rule.priority || "0"}
                                          onchange={(e) => handleRuleChange(index, "priority", e.target.value)}
                                          min={0}
                                        />
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                          Higher priority rules are applied first
                                        </small>
                                      </div>
                                      <div className="col-md-12">
                                        <div 
                                          style={{
                                            padding: '12px',
                                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                            borderRadius: '8px',
                                            border: '1px solid ' + (isDarkMode ? '#404040' : '#dee2e6')
                                          }}
                                        >
                                          <ToggleSwitch
                                            isChecked={rule.isActive}
                                            onToggle={(checked) =>
                                              handleRuleChange(index, "isActive", checked)
                                            }
                                            label="Rule Status"
                                            name="isActive"
                                            themeColor={themeColor}
                                          />
                                          <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem', marginLeft: '50px' }}>
                                            Only active rules will be applied to reservations
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Date Range Section */}
                                  <div 
                                    className="mb-4"
                                    style={{
                                      padding: '20px',
                                      backgroundColor: isDarkMode ? '#374151' : '#f8f9fa',
                                      borderRadius: '12px',
                                      border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                                    }}
                                  >
                                      <div className="d-flex align-items-center mb-3">
                                        <i className="fa fa-calendar-alt me-2" style={{ color: themeColor }}></i>
                                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                                          Date Range (Optional)
                                        </h6>
                                      </div>
                                      <div 
                                        style={{
                                          padding: '12px',
                                          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                          borderRadius: '8px',
                                          border: '1px solid ' + (isDarkMode ? '#404040' : '#dee2e6')
                                        }}
                                      >
                                        <ToggleSwitch
                                          isChecked={rule.enableDateRange || false}
                                          onToggle={(checked) => {
                                            const updatedRules = [...rules];
                                            updatedRules[index] = {
                                              ...updatedRules[index],
                                              enableDateRange: checked,
                                              dateRange: checked
                                                ? {
                                                    startDateTime: moment(),
                                                    endDateTime: moment(),
                                                  }
                                                : {
                                                    startDateTime: null,
                                                    endDateTime: null,
                                                  },
                                              _isUnsaved: true,
                                            };
                                            setRules(updatedRules);
                                          }}
                                          label="Enable Date Range"
                                          name="enableDateRange"
                                          themeColor={themeColor}
                                        />
                                        <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem', marginLeft: '50px' }}>
                                          Apply this rule only within a specific date range
                                        </small>
                                      </div>
                                      {rule.enableDateRange && (
                                        <div className="row mt-3">
                                          <div className="col-md-6">
                                            <label className="form-label" style={{ fontWeight: '500' }}>
                                              <i className="fa fa-calendar-check me-1" style={{ color: themeColor }}></i>
                                              Start Date
                                            </label>
                                            <CombinedDateTimePicker
                                              value={rule.dateRange?.startDateTime}
                                              onChange={(value) =>
                                                handleRuleChange(index, "dateRange.startDateTime", value)
                                              }
                                              showTime={false}
                                            />
                                          </div>
                                          <div className="col-md-6">
                                            <label className="form-label" style={{ fontWeight: '500' }}>
                                              <i className="fa fa-calendar-times me-1" style={{ color: themeColor }}></i>
                                              End Date
                                            </label>
                                            <CombinedDateTimePicker
                                              value={rule.dateRange?.endDateTime}
                                              onChange={(value) =>
                                                handleRuleChange(index, "dateRange.endDateTime", value)
                                              }
                                              showTime={false}
                                            />
                                          </div>
                                        </div>
                                      )}
                                  </div>

                                  {/* Min/Max Deposit Section */}
                                  <div 
                                    className="mb-4"
                                    style={{
                                      padding: '20px',
                                      backgroundColor: isDarkMode ? '#374151' : '#f8f9fa',
                                      borderRadius: '12px',
                                      border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                                    }}
                                  >
                                      <div className="d-flex align-items-center mb-3">
                                        <i className="fa fa-sliders-h me-2" style={{ color: themeColor }}></i>
                                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                                          Deposit Limits (Optional)
                                        </h6>
                                      </div>
                                      <div className="row g-3">
                                        <div className="col-md-6">
                                          <div 
                                            style={{
                                              padding: '12px',
                                              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                              borderRadius: '8px',
                                              border: '1px solid ' + (isDarkMode ? '#404040' : '#dee2e6')
                                            }}
                                          >
                                            <ToggleSwitch
                                              isChecked={rule.enableMinDeposit}
                                              onToggle={(checked) =>
                                                handleRuleChange(index, "enableMinDeposit", checked)
                                              }
                                              label="Set Minimum Deposit"
                                              name="enableMinDeposit"
                                              themeColor={themeColor}
                                            />
                                            {rule.enableMinDeposit && (
                                              <div className="mt-3">
                                                <FormInput
                                                  label={`Minimum Deposit (${getCurrencySymbol(compCurrency)})`}
                                                  placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                                                  type="number"
                                                  value={rule.minDepositValue || ""}
                                                  onchange={(e) =>
                                                    handleRuleChange(index, "minDepositValue", e.target.value)
                                                  }
                                                  step="0.01"
                                                  min={0}
                                                />
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div 
                                            style={{
                                              padding: '12px',
                                              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                              borderRadius: '8px',
                                              border: '1px solid ' + (isDarkMode ? '#404040' : '#dee2e6')
                                            }}
                                          >
                                            <ToggleSwitch
                                              isChecked={rule.enableMaxDeposit}
                                              onToggle={(checked) =>
                                                handleRuleChange(index, "enableMaxDeposit", checked)
                                              }
                                              label="Set Maximum Deposit"
                                              name="enableMaxDeposit"
                                              themeColor={themeColor}
                                            />
                                            {rule.enableMaxDeposit && (
                                              <div className="mt-3">
                                                <FormInput
                                                  label={`Maximum Deposit (${getCurrencySymbol(compCurrency)})`}
                                                  placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                                                  type="number"
                                                  value={rule.maxDepositValue || ""}
                                                  onchange={(e) =>
                                                    handleRuleChange(index, "maxDepositValue", e.target.value)
                                                  }
                                                  step="0.01"
                                                  min={0}
                                                />
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                  </div>

                                  {/* Weekly Schedule Section */}
                                  <div 
                                    className="mb-4"
                                    style={{
                                      padding: '20px',
                                      backgroundColor: isDarkMode ? '#374151' : '#f8f9fa',
                                      borderRadius: '12px',
                                      border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                                    }}
                                  >
                                      <div className="d-flex align-items-center mb-3">
                                        <i className="fa fa-clock me-2" style={{ color: themeColor }}></i>
                                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                                          Weekly Schedule (Optional)
                                        </h6>
                                      </div>
                                      <div 
                                        style={{
                                          padding: '12px',
                                          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                          borderRadius: '8px',
                                          border: '1px solid ' + (isDarkMode ? '#404040' : '#dee2e6')
                                        }}
                                      >
                                        <ToggleSwitch
                                          isChecked={rule.enableSchedule}
                                          onToggle={(checked) =>
                                            handleRuleChange(index, "enableSchedule", checked)
                                          }
                                          label="Enable Weekly Schedule"
                                          name="enableSchedule"
                                          themeColor={themeColor}
                                        />
                                        <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem', marginLeft: '50px' }}>
                                          Set specific days and times when this rule applies
                                        </small>
                                      </div>
                                      {rule.enableSchedule && (
                                        <div className="mt-3">
                                          <div 
                                            style={{
                                              padding: '12px',
                                              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                              borderRadius: '8px',
                                              border: '1px solid ' + (isDarkMode ? '#404040' : '#dee2e6'),
                                              marginBottom: '16px'
                                            }}
                                          >
                                            <ToggleSwitch
                                              isChecked={rule.useSameTimeSlots}
                                              onToggle={(checked) => {
                                                handleRuleChange(index, "useSameTimeSlots", checked);
                                                if (checked) {
                                                  applyCommonTimeSlotToAllForRule(index);
                                                }
                                              }}
                                              label="Use same time slots for all days"
                                              name="useSameTimeSlots"
                                              themeColor={themeColor}
                                            />
                                          </div>
                                          <div 
                                            style={{
                                              padding: '16px',
                                              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                                              borderRadius: '8px',
                                              border: '1px solid ' + (isDarkMode ? '#404040' : '#dee2e6')
                                            }}
                                          >
                                            {rule.useSameTimeSlots ? (
                                              <CommonTimeSlotForAllWeek
                                                setCommonTimeSlots={(slots) =>
                                                  handleRuleChange(index, "commonTimeSlot", slots)
                                                }
                                                commonTimeSlots={rule.commonTimeSlot || []}
                                                isProfilePage={false}
                                                preferTimePickerDropUp={true}
                                              />
                                            ) : (
                                              <ScheduleManager
                                                schedules={
                                                  Array.isArray(rule.schedules)
                                                    ? rule.schedules
                                                    : []
                                                }
                                                setSchedules={(schedulesOrUpdater) => {
                                                  const current = Array.isArray(rule.schedules)
                                                    ? rule.schedules
                                                    : [];
                                                  const next =
                                                    typeof schedulesOrUpdater === "function"
                                                      ? schedulesOrUpdater(current)
                                                      : schedulesOrUpdater;
                                                  handleRuleChange(
                                                    index,
                                                    "schedules",
                                                    Array.isArray(next) ? next : current
                                                  );
                                                }}
                                                isProfilePage={false}
                                                isDarkMode={isDarkMode}
                                              />
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
      </GridSectionLoader>

      <ServiceTimeModal
        isOpen={isServiceTimeModalOpen}
        onClose={() => setIsServiceTimeModalOpen(false)}
        onSubmit={handleServiceTimeSubmit}
        servicePeriods={servicePeriods}
        selectedServiceTimes={selectedServiceTimes}
        fetchServicePeriods={fetchServicePeriods}
      />

      <TableQrModal
        isOpen={qrModalOpen}
        onClose={() => {
          setQrModalOpen(false);
          setQrTableId(null);
          setQrBulkMode(false);
        }}
        CompID={CompID}
        tableId={qrBulkMode ? null : qrTableId}
        serviceAreaId={parseInt(sA_ID, 10) || formData?.id || null}
        serviceAreaName={formData?.sA_Name || ""}
      />
    </div>
  );
}

export default UpdateDiningArea;
